import React from "react";
import { Platform, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  CTA,
  FlexGrid,
  Heading,
  Text,
  formatCurrency,
  useResponsive,
  Button,
  theme,
} from "@clearabee/ui-library-native";
import {
  useCatalogue,
  getCalloutCharge,
  getItemDescendants,
} from "@clearabee/ui-sdk";
import { Anchor } from "components/Core/Anchor/Anchor";
import { styles, ids } from "./Skips.styles";
import placeholderImage from "lib/images/placeholder.png";
import { Image } from "components/Image/Image";
import { ManVanCard } from "components/Cards/ManVanCard";
import { useQuestionContext } from "lib/providers/questionProvider";
import { FormState } from "./Skips.validation";
import { CatalogueItem } from "lib/types";
import { useAnalytics } from "lib/hooks/useAnalytics";

const sortByPriceAndSize = (items: CatalogueItem[]): CatalogueItem[] => {
  return items.sort(
    (a, b) =>
      Number(a.meta?.yards ?? a.price ?? 0) -
      Number(b.meta?.yards ?? b.price ?? 0),
  );
};

export const SelectExchangeGrid = (): React.ReactElement => {
  const [translate] = useTranslation();
  const { defaultCatalogue, getCatalogue } = useCatalogue();
  const context = useQuestionContext<FormState>();
  const refAndPostcode = context.get("refAndPostcode");
  const wasteType = context.get("wasteType");
  const catalogue =
    getCatalogue(refAndPostcode?.postcode ?? "") || defaultCatalogue;
  const service = catalogue.items.find(({ sku }) => sku === "SKIPS");
  const { screens } = useResponsive();
  const { ecommerce } = useAnalytics();

  const analyticData = {
    item_list_name: `SKIPS Product List`,
    item_list_id: `SKIPS_product_list`,
  };

  const products = sortByPriceAndSize(
    getItemDescendants("SKIPS", catalogue.items).filter(
      ({ meta, sku }) =>
        !(
          ["Soil", "HeavyConstruction"].includes(wasteType) &&
          parseInt(meta?.yards) > 8
        ) && !sku.startsWith(refAndPostcode?.previousSkip ?? ""),
    ),
  );

  const isExtended = products.length >= 5;
  const extension =
    (screens.large && !isExtended) || (screens.xlarge && isExtended);

  return (
    <FlexGrid
      cellSpacing={screens.medium ? "xsmall" : "xsmall2"}
      rowSpacing={screens.medium ? "xsmall" : "xsmall2"}
      wrap={extension ? "nowrap" : "wrap"}
      style={[{ marginTop: theme.spacing.small }, styles.flexGrid]}
      dataSet={{ media: ids.flexGrid }}
    >
      {products.flatMap((product) => {
        if (!product.price) return [];
        const { title, price, img, meta, sku, description } = product;
        return (
          <FlexGrid.Cell
            key={title}
            base="50%"
            medium="33.33%"
            large="33.33%"
            xlarge="16.666%"
            xlarge2="16.666%"
            xlarge3="16.666%"
            xlarge4="16.666%"
          >
            <Anchor
              styles={{ flex: 1 }}
              routeName="ProductPage"
              params={{
                categorySku: "SKIPS",
                productSku: sku,
                postcode: refAndPostcode?.postcode,
                exchangeOrderRef: refAndPostcode?.ref,
                wasteType,
              }}
              onClick={() => {
                ecommerce.selectItem(
                  product,
                  analyticData.item_list_name,
                  analyticData.item_list_id,
                );
              }}
            >
              <CTA
                orientation="portrait"
                shadow={Platform.OS === "web"}
                heading={
                  <Heading
                    level={4}
                    styles={styles.CTAHeading}
                    dataSet={{ media: ids.CTAHeading }}
                    numberOfLines={screens.medium ? 1 : undefined}
                  >
                    {title}
                  </Heading>
                }
                subheading={
                  <View style={styles.subheadingContainer}>
                    {!!price && (
                      <Text
                        styles={styles.subheading}
                        dataSet={{ media: ids.subheading }}
                      >
                        {formatCurrency(
                          price + getCalloutCharge(catalogue.items, service!),
                        )}
                        <Text
                          styles={{
                            fontWeight: "100",
                          }}
                        >
                          {" "}
                          {translate("common.incVat")}
                        </Text>
                      </Text>
                    )}
                  </View>
                }
                textColor="brand"
                image={
                  <Image
                    source={img ?? placeholderImage}
                    resizeMode="contain"
                    style={styles.CTAImage}
                  />
                }
                pill={
                  meta?.popular ? translate("common.mostPopular") : undefined
                }
                background="light"
                styles={styles.CTA}
                dataSet={{ media: ids.CTA }}
              >
                {screens.medium && (
                  <>
                    <Text fontSize="xsmall" styles={styles.descriptionText}>
                      {description}
                    </Text>
                    <Button
                      color="warning"
                      size="medium"
                      styles={styles.button}
                    >
                      <Text
                        fontSize="small"
                        styles={{ fontFamily: "ProximaSemiBold" }}
                      >
                        {translate("common.bookNow")}
                      </Text>
                    </Button>
                  </>
                )}
                {!screens.medium && !!description && (
                  <Text
                    fontSize="xsmall"
                    styles={styles.mobileDescription}
                    dataSet={{ media: ids.mobileDescription }}
                  >
                    {description}
                  </Text>
                )}
              </CTA>
            </Anchor>
          </FlexGrid.Cell>
        );
      })}
      <FlexGrid.Cell
        base="100%"
        medium={undefined}
        large="33.33%"
        xlarge="16.666%"
      >
        <ManVanCard />
      </FlexGrid.Cell>
    </FlexGrid>
  );
};
